/**
 * @engine/vfx — VFX graph system: explicit public surface.
 *
 * Only the surface real consumers use is exported. The compiler (except
 * `compileVFXGraph`), the GPU renderers (`VFXSystemRenderer`/`VFXMultiSystemRenderer`),
 * WGSL/binding constants (except `VFX_MAX_PARTICLES`), the node-def arrays, and the
 * validator (except `canConnectVFXPorts`) are **package-internal** — imported directly
 * by module path within @engine/vfx, never re-exported. No `export *` of implementation
 * modules (only the cohesive `./types` authoring-model barrel is re-exported wholesale).
 */

// ---- Authoring model: data types, context/port/node-def types, `dataPort`/`flowPort` ----
export * from './types';

// ---- Editor authoring API ----
export { VFXGraph } from './VFXGraph';
export { VFXNodeRegistry, vfxNodeRegistry } from './VFXNodeRegistry';
export { canConnectVFXPorts } from './VFXGraphValidator';
export { compileVFXGraph } from './compiler/VFXCompiler';
// Public contract of `compileVFXGraph` (the rest of the compiler stays internal).
export type { VFXCompilationResult, VFXCompilationError, VFXErrorCode } from './compiler/types';
// Slang compile service seam — hosts inject their Slang→WGSL toolchain
// (editor: slang-wasm worker/IPC; tests: slangc CLI) before compiling graphs.
export { setVFXSlangCompiler, hasVFXSlangCompiler } from './compiler/SlangCompileService';
// Graph-variable slot resolution — single source of truth shared by the shader
// codegen and every runtime writer (component overrides, name-based setters).
export { resolveVariableSlot, variableCategory } from './compiler/codegen/variableSlots';
export type { ResolvedVariableSlot, VariableSlotCategory } from './compiler/codegen/variableSlots';
export type {
  VFXSlangCompileFn,
  VFXSlangCompileResult,
  VFXSlangDiagnostic,
} from './compiler/SlangCompileService';
export { registerBuiltinVFXNodes } from './nodes';
export { VFX_MAX_PARTICLES } from './VFXBindings';

// ---- Content pipeline (asset import + (de)serialization) ----
export { VFX_IMPORTER, toVFXDocument, serializeVFXGraph, deserializeVFXGraph } from './VFXGraphAsset';
export type { CompiledVFXBundle } from './VFXGraphAsset';

// ---- Presets (editor "New VFX from template" gallery) ----
export {
  createEmptyVFXGraph,
  createFireworkVFXGraph,
  createCosmicAuroraVFX,
  createElectricStormVFX,
  createMagicPortalVFX,
  createPhoenixRisingVFX,
  createWaterRippleGridVFX,
  createSingularityVFX,
  createBlackHoleVFX,
  createTornadoVFX,
  createForceFieldVFX,
  createSmokeVFX,
  createExplosion2DVFX,
} from './VFXGraphAsset';

// ---- Texture utilities (runtime + editor) ----
export { loadTextureFromURL, loadDefaultVFXTexture, DEFAULT_TEXTURE_PATH } from './textureUtils';

// ---- Point-map pipeline (editor asset handlers) ----
export {
  createDefaultPointMapDefinition,
  createSettingsHash,
  serializePointMap,
  deserializePointMap,
} from './PointMapAsset';
export type { PointMapDefinition, CompiledPointMapMeta } from './PointMapAsset';
export { bakePointMapFromPixelData } from './PointMapBaker';
export type { PointMapResult } from './PointMapBaker';
